<script setup>
import { computed, watch } from 'vue';
import { useFormContext } from '/app/composables/form';
import NTextarea from '/app/components/form/Textarea.vue';

const regExpCause = new RegExp(/What(.*)could|How(.*)could/, 'i');

const causes = [
    { title: 'What could', match: /What(.*)could/ },
    { title: 'How could', match: /How(.*)could/ },
];

const props = defineProps({
    initialValue: {
        type: String,
    },
    placeholder: {
        type: String,
        default: 'Type here to fill in the rest of your question',
    },
    forceError: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    required: {
        type: Boolean,
        default: false,
    },
    id: {
        type: String,
        default: 'question_title',
    },
    maxLength: {
        type: Number,
        default: 180,
    },
});

const emit = defineEmits(['blur']);

const questionTitle = defineModel({
    type: String,
    default: '',
});

const form = useFormContext();

const hasInitialWords = computed(() => regExpCause.test(questionTitle.value));

function changeCause(cause) {
    let title = questionTitle.value;

    if (!hasInitialWords.value) {
        title = cause + ' ' + title;
    }

    questionTitle.value = title.replace(regExpCause, cause);
}

function isActive(cause) {
    return cause.test(questionTitle.value);
}

function valid() {
    const text = [];

    if (!hasInitialWords.value) {
        text.push('either "How could" or "What could"');
    }

    if (text.length) {
        form.errors.set(
            props.id,
            `Your title should contain ${text.join(' and contain ')}`,
        );
    } else {
        form.errors.clear(props.id);
    }
}

watch(questionTitle, () => {
    form.validate();
});

// Equivalent of `created()` in Options API — initialize the model with the
// initial value or the default "What could" prefix on first setup.
questionTitle.value = props.initialValue || 'What could';

defineExpose({ valid });
</script>

<template>
    <div class="tab-input has-question-mark">
        <div class="tab-input__header">
            <div class="tab-input__tabs">
                <button aria-label="button" class="tab-input__tab" v-for="(cause, index) in causes" :key="index" :class="{
                    'is-active shadow-sm shadow-black': isActive(cause.match),
                    '!bg-red-500': (forceError === true && isActive(cause.match)) || (isActive(cause.match) && !hasInitialWords),
                }" :disabled="disabled" @click.prevent="changeCause(cause.title)">
                    {{ cause.title }}...
                </button>
            </div>
        </div>
        <div class="tab-input__content">
            <div class="tab-input__field">
                <NTextarea v-model="questionTitle" :id="id" :name="id" :placeholder="placeholder" :maxlength="maxLength"
                    :force-error="forceError === true || !hasInitialWords" :disabled="disabled" :required="required"
                    class="" @blur="emit('blur', $event)" @keydown.tab="emit('blur', $event)">
                    <template #error="{ error, invalid }">
                        <div class="flex items-center justify-between flex-wrapper pt-2 gap-4">
                            <span v-if="invalid && error" class="form-group__error !mt-0 !p-0">{{ error }}</span>
                            <div v-if="questionTitle" class="tab-input__char-counter text-sm text-right px-1 ml-auto">
                                <b>{{ maxLength - questionTitle.length }}</b> characters remaining
                            </div>
                        </div>
                    </template>
                </NTextarea>
            </div>
        </div>
    </div>
</template>
